<template>
  <div class="review-action-row" :class="{ 'review-action-row_off': !value.isEnabled }">
    <v-icon
      class="review-action-row__grip"
      :class="{ 'review-action-row__grip_disabled': disabled }"
      title="Перетащить"
    >
      mdi-drag-vertical
    </v-icon>

    <span class="review-action-row__pos">{{ index + 1 }}</span>

    <div class="review-action-row__logos">
      <font-awesome-icon
        v-for="icon in icons"
        :key="icon.key"
        class="review-action-row__logo"
        :icon="icon.icon"
        :style="{ background: icon.color }"
      />
    </div>

    <div class="review-action-row__name">
      <div class="review-action-row__title">
        {{ value.name }}
      </div>
      <div class="review-action-row__subtitle">
        {{ subtitle }}
      </div>
    </div>

    <div class="review-action-row__links">
      <template v-if="value.type === 'service'">
        <review-action-link
          v-for="service in value.services"
          :key="service.type"
          :value="service.link"
          :default-value="service.defaultLink"
          :label="value.services.length > 1 ? service.title : null"
          :disabled="disabled"
          :highlight-empty="value.isEnabled"
          @input="setServiceLink(service.type, $event)"
        />
      </template>

      <template v-else-if="value.type === 'market'">
        <review-action-link
          v-for="market in value.markets"
          :key="market.type"
          :value="market.link"
          :label="market.title"
          :editable="false"
        />
      </template>

      <review-action-link
        v-else
        :value="value.link"
        :default-value="value.defaultLink"
        :disabled="disabled"
        :highlight-empty="value.isEnabled"
        @input="setLink"
      />
    </div>

    <v-switch
      class="review-action-row__switch"
      :input-value="value.isEnabled"
      :disabled="disabled"
      hide-details
      inset
      dense
      @change="setEnabled"
    />

    <div
      v-if="warning"
      class="review-action-row__warn"
      :class="{ 'review-action-row__warn_soft': warning.soft }"
    >
      <v-icon small :color="warning.soft ? '#9a9ea6' : 'primary'">
        {{ warning.soft ? 'mdi-information-outline' : 'mdi-alert' }}
      </v-icon>
      <span>{{ warning.text }}</span>
    </div>
  </div>
</template>

<script>
  import ReviewActionLink from '@/components/settings/review-actions/ReviewActionLink.vue'

  function effectiveLink (own, fallback) {
    if (own === null || own === undefined) return fallback || null
    return own === '' ? null : own
  }

  const SERVICE_ICONS = {
    '2gis': { icon: ['fas', 'map-marked-alt'], color: '#19aa1e' },
    vk: { icon: ['fab', 'vk'], color: '#0077ff' },
    yandex_maps: { icon: ['fab', 'yandex'], color: '#fc3f1d' },
    google_maps: { icon: ['fab', 'google'], color: '#34a853' },
  }

  export default {
    name: 'ReviewActionRow',
    components: { ReviewActionLink },
    props: {
      value: { type: Object, required: true },
      index: { type: Number, required: true },
      disabled: Boolean,
    },
    computed: {
      icons () {
        if (this.value.type === 'service') {
          return this.value.services.map((s) => ({ key: s.type, ...(SERVICE_ICONS[s.type] || { icon: ['fas', 'comment-dots'], color: '#6d6d6d' }) }))
        }
        if (this.value.type === 'market') {
          return [{ key: 'market', icon: ['fas', 'star'], color: '#3d00fe' }]
        }
        return [{ key: 'link', icon: ['fas', 'link'], color: '#6d6d6d' }]
      },
      subtitle () {
        if (this.value.type === 'market') return 'Ссылки общие для сети · без правки'
        if (this.value.type === 'link') return 'Любая ссылка: сайт-отзовик, форма'
        if (this.value.services.length > 1) return `Составной: клиент выбирает один из ${this.value.services.length} сервисов`
        return 'Сервис отзывов'
      },
      // клиент видит свою ссылку филиала; null - сетевую по умолчанию; '' - ничего (отключена)
      filledServices () {
        return (this.value.services || []).filter((s) => effectiveLink(s.link, s.defaultLink))
      },
      hasAnyLink () {
        if (this.value.type === 'market') return true
        if (this.value.type === 'link') return !!effectiveLink(this.value.link, this.value.defaultLink)
        return this.filledServices.length > 0
      },
      warning () {
        if (!this.value.isEnabled) return null
        if (!this.hasAnyLink) {
          return { soft: false, text: 'Внешний сервис включён, но без ссылки клиенту не покажется - очередь перейдёт к следующему.' }
        }
        if (this.value.type === 'service' && this.filledServices.length < this.value.services.length) {
          const names = this.filledServices.map((s) => s.title).join(', ')
          return { soft: true, text: `Клиенту покажется только ${names}: сервис без ссылки из выбора выпадает.` }
        }
        return null
      },
    },
    methods: {
      emit (patch) {
        this.$emit('input', { ...this.value, ...patch })
      },
      setEnabled (isEnabled) {
        this.emit({ isEnabled: !!isEnabled })
      },
      setLink (link) {
        this.emit({ link })
      },
      setServiceLink (type, link) {
        this.emit({ services: this.value.services.map((s) => (s.type === type ? { ...s, link } : s)) })
      },
    },
  }
</script>

<style scoped lang="less">
.review-action-row {
  display: grid;
  // название уже ссылок; последняя колонка шире переключателя, чтобы отступ от ссылок был 24px
  grid-template-columns: 28px 22px minmax(40px, auto) minmax(200px, 0.6fr) minmax(320px, 1.6fr) 76px;
  align-items: center;
  gap: 12px;
  padding: 10px 14px 10px 6px;
  border: 1px solid #e6e9ee;
  border-radius: 6px;
  background: #fff;
  transition: border-color 0.15s;

  &:hover { border-color: #d6d6d7; }

  &_off {
    background: #fafbfc;

    .review-action-row__name { opacity: 0.6; }
    .review-action-row__logos { opacity: 0.6; filter: saturate(0); }

    // приглушаем только строки ссылок в покое: открытая форма правки должна быть читаемой
    .review-action-link:not(.review-action-link_editing) { opacity: 0.55; }
  }

  &_ghost {
    opacity: 0.4;
    background: #fff3e0;
  }

  &__grip {
    cursor: grab;
    color: #9a9ea6;

    &:active { cursor: grabbing; }
    &_disabled { cursor: default; opacity: 0.4; }
  }

  &__pos {
    font-size: 13px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    text-align: center;
    color: @color-primary;
  }

  &_off &__pos { color: #9a9ea6; }

  &__logos { display: flex; }

  // круг 40px, глиф 18px по центру; у составного сервиса круги накладываются стопкой
  &__logo {
    box-sizing: border-box;
    width: 40px;
    height: 40px;
    padding: 11px;
    border-radius: 50%;
    color: #fff;
    flex: none;

    & + & {
      margin-left: -10px;
      box-shadow: 0 0 0 2px #fff;
    }
  }

  &__name { min-width: 0; }
  &__title { font-size: 15px; font-weight: 500; line-height: 1.25; }
  &__subtitle { font-size: 12px; color: #6d6d6d; margin-top: 2px; }

  &__links { display: flex; flex-direction: column; gap: 5px; min-width: 0; }

  &__switch { justify-self: end; margin: 0; padding: 0; }

  &__warn {
    grid-column: ~"2 / -1"; // LESS иначе считает это делением
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    color: #8a4b00;
    background: #fff3e0;

    &_soft { color: #6d6d6d; background: #f1f3f6; }
  }
}

@media (max-width: 960px) {
  .review-action-row {
    grid-template-columns: 28px 22px minmax(40px, auto) 1fr 76px;

    &__links { grid-column: ~"3 / 5"; }
  }
}
</style>
